/**
 * Water Solutions history + invoice generation.
 *
 * Shown as a tab in Wash Station. Supports:
 *   • Filter by customer, month and status (LOGGED / INVOICED).
 *   • "Log Water Solution" shortcut back to the main dialog.
 *   • "Generate Invoice" for the currently filtered customer + month —
 *     dry-runs via /water-solutions/generate-invoice, confirms the total
 *     + line count with the operator, then commits.
 *   • Open the resulting invoice in the Invoices tab after commit.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Droplets, FileText, Loader2, RefreshCw, Trash2, ExternalLink, Receipt,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const STATUS_COLORS = {
  LOGGED: "bg-blue-50 text-blue-800 border-blue-200",
  INVOICED: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export default function WaterSolutionsHistory({ onLogNew }) {
  const { user } = useAuth();
  const canManage = ["admin", "general_manager"].includes(user?.role);
  const canInvoice = ["admin", "general_manager", "supervisor"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Filter state. Defaults: STOLT (most-used customer), current month, all statuses.
  const [customer, setCustomer] = useState("STOLT");
  const [yyyyMm, setYyyyMm] = useState(new Date().toISOString().slice(0, 7));
  const [status, setStatus] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (customer) params.set("customer", customer);
      if (yyyyMm) params.set("yyyy_mm", yyyyMm);
      if (status) params.set("status", status);
      const [{ data: items }, { data: ps }] = await Promise.all([
        api.get(`/water-solutions?${params.toString()}`),
        api.get("/partners?active=true&role=carrier"),
      ]);
      setRows(items || []);
      setPartners(Array.isArray(ps) ? ps : (ps?.items || []));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [customer, yyyyMm, status]);

  // Pre-compute the totals + "any LOGGED to invoice?" check so the
  // Generate Invoice button can be disabled cleanly.
  const summary = useMemo(() => {
    const loggedRows = rows.filter((r) => r.status === "LOGGED");
    const loggedTotal = loggedRows.reduce((s, r) => s + (Number(r.flat_fee) || 0), 0);
    const invoicedTotal = rows
      .filter((r) => r.status === "INVOICED")
      .reduce((s, r) => s + (Number(r.flat_fee) || 0), 0);
    const currency = rows[0]?.currency || "USD";
    const sym = currency === "EUR" ? "€" : "$";
    return {
      currency, sym,
      loggedCount: loggedRows.length,
      loggedTotal,
      invoicedCount: rows.length - loggedRows.length,
      invoicedTotal,
    };
  }, [rows]);

  const generateInvoice = async () => {
    if (!customer || !yyyyMm) { toast.error("Pick a carrier and a month"); return; }
    if (summary.loggedCount === 0) { toast.warning("No LOGGED Water Solutions in this filter"); return; }
    setGenerating(true);
    try {
      // Dry-run preview
      const { data: preview } = await api.get(
        `/water-solutions/generate-invoice?customer=${customer}&yyyy_mm=${yyyyMm}`
      );
      if (preview.line_count === 0) { toast.warning("Nothing to invoice."); return; }
      const sym = preview.currency === "EUR" ? "€" : "$";
      if (!window.confirm(
        `Generate Water Solution invoice for ${customer} / ${yyyyMm}?\n\n` +
        `• ${preview.line_count} service${preview.line_count === 1 ? "" : "s"}\n` +
        `• Total: ${sym}${preview.subtotal.toFixed(2)} ${preview.currency}\n\n` +
        `All ${preview.line_count} rows will be marked INVOICED. Nothing is sent to the customer automatically — a DRAFT invoice is created in Invoices.`
      )) return;
      const { data: inv } = await api.post(
        `/water-solutions/invoice?customer=${customer}&yyyy_mm=${yyyyMm}`
      );
      toast.success(`Invoice ${inv.invoice_no} created — ${sym}${inv.total.toFixed(2)}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGenerating(false); }
  };

  const del = async (ws) => {
    if (!window.confirm(`Delete Water Solution for tank ${ws.tank_number} (PO ${ws.po_number})?`)) return;
    try {
      await api.delete(`/water-solutions/${ws.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="water-solutions-history">
      <div className="bg-white border border-gray-200 p-4 flex flex-wrap items-end justify-between gap-3">
        <div className="grid grid-cols-3 gap-3 flex-1 min-w-[500px]">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Carrier</label>
            <select value={customer} onChange={(e) => setCustomer(e.target.value)} className="ps-input" data-testid="ws-history-customer">
              <option value="">All carriers</option>
              {partners.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.code}{p.legal_name && p.legal_name !== p.code ? ` · ${p.legal_name}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Month</label>
            <input type="month" value={yyyyMm} onChange={(e) => setYyyyMm(e.target.value)} className="ps-input font-mono" data-testid="ws-history-month" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="ps-input" data-testid="ws-history-status">
              <option value="">All statuses</option>
              <option value="LOGGED">LOGGED (awaiting invoice)</option>
              <option value="INVOICED">INVOICED</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2 items-end">
          <button onClick={load} disabled={loading} className="ps-btn ps-btn-secondary">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
          <button onClick={onLogNew} className="ps-btn ps-btn-secondary" data-testid="ws-history-log-new">
            <Droplets className="w-4 h-4" /> Log new
          </button>
          {canInvoice && (
            <button
              onClick={generateInvoice}
              disabled={generating || summary.loggedCount === 0 || !customer || !yyyyMm}
              className="ps-btn ps-btn-primary"
              data-testid="ws-history-generate-invoice"
              title={summary.loggedCount === 0
                ? "No LOGGED services in the current filter"
                : `Invoice ${summary.loggedCount} service(s) for ${customer} / ${yyyyMm}`}
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
              Generate Invoice
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 p-4">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500 mb-1">LOGGED — ready to invoice</div>
          <div className="font-black text-2xl tracking-tight tabular-nums">
            {summary.sym}{summary.loggedTotal.toFixed(2)}
            <span className="text-sm font-normal text-gray-500 ml-2">· {summary.loggedCount} service{summary.loggedCount === 1 ? "" : "s"}</span>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-4">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500 mb-1">INVOICED</div>
          <div className="font-black text-2xl tracking-tight tabular-nums">
            {summary.sym}{summary.invoicedTotal.toFixed(2)}
            <span className="text-sm font-normal text-gray-500 ml-2">· {summary.invoicedCount} service{summary.invoicedCount === 1 ? "" : "s"}</span>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200">
        <table className="ps-table text-xs" data-testid="ws-history-table">
          <thead>
            <tr>
              <th>Service date</th>
              <th>Carrier</th>
              <th>Tank</th>
              <th>PO</th>
              <th className="text-right">Fee</th>
              <th>Status</th>
              <th>Invoice</th>
              <th>Remarks</th>
              <th>Logged by</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`ws-row-${r.id}`}>
                <td className="font-mono">{r.service_date}</td>
                <td className="font-mono">{r.customer_code}</td>
                <td className="font-mono font-bold">{r.tank_number}</td>
                <td className="font-mono">{r.po_number}</td>
                <td className="text-right font-mono">
                  {r.currency === "EUR" ? "€" : "$"}{Number(r.flat_fee).toFixed(2)}
                </td>
                <td>
                  <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-bold uppercase ${STATUS_COLORS[r.status] || "bg-gray-50 text-gray-700 border-gray-200"}`}>
                    {r.status}
                  </span>
                </td>
                <td className="font-mono text-xs">
                  {r.invoice_id ? (
                    <a href={`/invoices?id=${r.invoice_id}`} target="_blank" rel="noreferrer" className="text-[#006E9E] hover:underline inline-flex items-center gap-1">
                      View <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : "—"}
                </td>
                <td className="text-gray-600 max-w-xs truncate" title={r.remarks || ""}>{r.remarks || "—"}</td>
                <td className="text-gray-500 text-[11px]">{r.created_by}</td>
                <td className="text-right">
                  {canManage && r.status === "LOGGED" && (
                    <button onClick={() => del(r)} className="text-red-600 hover:text-red-800" title="Delete" data-testid={`ws-del-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={10} className="text-center text-gray-400 py-10">
                No Water Solutions match this filter. Try clearing the status or month, or
                <button onClick={onLogNew} className="underline text-[#006E9E] ml-1">log a new one</button>.
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={10} className="text-center text-gray-400 py-10">
                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
